import {
  Button,
  Flashbar,
  PropertyFilter,
  Table,
} from '@cloudscape-design/components';
import { PartTypes, PPartStatus } from '@parallel-fluidics/shared/constants';
import { getPPartId } from '@parallel-fluidics/shared/models/projects/parts';
import PPartDisplay from 'components/Projects/pPartDisplay';
import PPartStatusBadge from 'components/Projects/pPartStatusBadge';
import getPistonSummary from 'features/pPart/utils/getPistonSummary';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import PartActionButton from './partActionButton';
import { usePPartData } from './pPartProvider';

export default function PartsTable() {
  const {
    pParts,
    status,
    projectDocId,
  } = usePPartData();
  const navigate = useNavigate();
  const location = useLocation();

  const [expandedItems, setExpandedItems] = useState([]);
  const [query, setQuery] = useState({
    tokens: [],
    operation: 'or',
  });

  const filterOptions = useMemo(() => {
    const typeOptions = Object.values(PartTypes).map((value) => (
      {
        propertyKey: 'type',
        value,
        label: value,
      }
    ));

    const statusOptions = Object.values(PPartStatus).map((statusValue) => ({
      propertyKey: 'status',
      value: statusValue,
      label: statusValue,
    }));

    return [
      ...typeOptions,
      ...statusOptions,
    ];
  }, []);

  const dynamicColumnDisplay = useCallback((item) => {
    if (item.isParentRow) {
      return <i>-- Multiple --</i>;
    }
    if (item.type === PartTypes.TOOL) {
      return `Cavities: ${item.cavities?.map((cavity) => cavity.name).join(', ')}, Instance: ${item.instance}`;
    }
    if (item.type === PartTypes.PISTON) {
      return getPistonSummary(item);
    }
    return [item.material, item.stepType].filter(Boolean).join(', ');
  }, []);

  const navigateToPartDetail = useCallback((partDocId) => {
    navigate(`/projects/${projectDocId}/part/${partDocId}${location.search}`);
  }, [navigate, projectDocId, location.search]);

  const columnDefinitions = useMemo(() => [
    {
      id: 'partId',
      header: 'Part ID',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        item.isParentRow
          ? item.partId
          : <Button variant="inline-link" onClick={() => navigateToPartDetail(item.id)}>{item.partId}</Button>
      ),
      isRowHeader: true,
      width: 100,
    },
    {
      id: 'revision',
      header: 'Revision',
      cell: (item) => item.revision,
      width: 100,
    },
    {
      id: 'pPart',
      header: 'Display ID',
      cell: (item) => (item.isParentRow ? '' : getPPartId(item)),
    },
    {
      id: 'name',
      header: 'Name',
      cell: (item) => item.name,
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item) => item.type,
      width: 100,
    },
    {
      id: 'info',
      header: 'Extra info',
      cell: (item) => dynamicColumnDisplay(item),
    },
    {
      id: 'notes',
      header: 'Notes',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        <PPartDisplay
          fieldName="notes"
          projectDocId={projectDocId}
          pPartDocId={item.id}
          fallbackMessage={item.notes}
        />
      ),
    },
    {
      id: 'metadata',
      header: 'Metadata',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => (
        <ul>
          {item.metadataArr?.map(({ key, value }) => (
            <li key={key}>
              {`${key}: ${value}`}
            </li>
          ))}
        </ul>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created At',
      // eslint-disable-next-line no-unsafe-optional-chaining
      cell: (item) => {
        const date = item.createdAt?.toDate();
        return date ? date.toLocaleString('en-US') : '';
      },
    },
    {
      id: 'status',
      header: 'Status',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => !item.isParentRow && <PPartStatusBadge status={item.status} />,
    },
    {
      id: 'actions',
      header: 'Actions',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: (item) => !item.isParentRow && (
        <PartActionButton
          part={item}
        />
      ),
    },
  ], [dynamicColumnDisplay, navigateToPartDetail, projectDocId]);

  const items = useMemo(() => {
    if (pParts.length === 0) return [];
    const groupByKeys = ['partId', 'revision'];

    let filteredParts = pParts;
    if (query.tokens.length > 0) {
      const predicate = query.operation === 'and'
        ? (part) => query.tokens.every((token) => part[token.propertyKey] === token.value)
        : (part) => query.tokens.some((token) => part[token.propertyKey] === token.value);
      filteredParts = pParts.filter(predicate);
    }

    return Object.values(
      filteredParts.reduce((acc, item) => {
        const key = groupByKeys.map((k) => item[k]).join('-');

        if (!acc[key]) {
          return {
            ...acc,
            [key]: {
              ...item,
              children: [],
            },
          };
        }

        const { children, id: docId, ...rest } = acc[key];

        if (children.length === 0) {
          children.push({ id: docId, ...rest });
        }
        children.push(item);

        return {
          ...acc,
          [key]: {
            ...acc[key], id: `${docId}parent`, isParentRow: true, name: '', notes: '',
          },
        };
      }, {}),
    );
  }, [pParts, query]);

  const trackBy = 'id';

  return (
    <Table
      variant="full-page"
      columnDefinitions={columnDefinitions}
      expandableRows={{
        getItemChildren: (item) => item.children ?? [],
        isItemExpandable: (item) => item.children?.length > 0,
        expandedItems,
        onExpandableItemToggle: ({ detail }) => setExpandedItems((prev) => {
          const next = new Set((prev ?? []).map((item) => item[trackBy]));
          if (detail.expanded) {
            next.add(detail.item[trackBy]);
          } else {
            next.delete(detail.item[trackBy]);
          }
          return [...next].map((itemId) => ({ [trackBy]: itemId }));
        }),
      }}
      trackBy={trackBy}
      items={items}
      loadingText="Loading..."
      loading={status === 'loading'}
      filter={(
        <PropertyFilter
          query={query}
          onChange={({ detail }) => setQuery(detail)}
          expandToViewport
          filteringOptions={filterOptions}
          filteringProperties={[
            {
              key: 'type',
              operators: ['='],
              propertyLabel: 'Type',
              groupValuesLabel: 'Types',
            },
            {
              key: 'status',
              operators: ['='],
              propertyLabel: 'Status',
              groupValuesLabel: 'Statuses',
            },
          ]}
        />
        )}
      empty={
        status === 'error' ? (
          <Flashbar items={[{
            header: 'Error loading parts',
            type: 'error',
          }]}
          />
        ) : 'No parts found'
      }
      stickyColumns={{ first: 2, last: 0 }}
      resizableColumns
    />
  );
}
